import { useMemo } from "react";
import { useGetInvoicesByStudentIdQuery } from "../api/invoiceApi";
import { Invoice } from "../types/invoiceTypes";
import { format } from "date-fns";

export interface StudentInvoiceSummary {
  totalAmount: number;
  totalPayable: number;
  paidAmount: number;
  pendingAmount: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  pendingInvoiceCount: number;
  totalDiscount: number;
}

export const useStudentInvoices = (studentId: string) => {
  const {
    data: invoicesResponse,
    isLoading,
    error,
    refetch,
  } = useGetInvoicesByStudentIdQuery(studentId);

  const invoices = useMemo(() => {
    return invoicesResponse?.data?.invoices || [];
  }, [invoicesResponse]);

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (e) {
      return dateString;
    }
  };

  // Get invoice by ID
  const getInvoiceById = (invoiceId: number): Invoice | undefined => {
    return invoices.find((invoice) => invoice.id === invoiceId);
  };

  // Transform invoices to a format suitable for the UI
  const invoiceRows = useMemo(() => {

    console.log(invoices, 'invoices')
    return (
      invoices
        .map((invoice) => {
          const paymentDate = "-";
          const paymentMode = "-";

          const rawAmount = parseFloat(invoice.amount) || 0;
          const discountVal = Number(invoice.discount_value) || 0;
          const discountAmount =
            invoice.discount_type === "percent"
              ? rawAmount * (discountVal / 100)
              : discountVal;
          const netPayable = Math.max(0, rawAmount - discountAmount);

          // Calculate total paid across online (completed) and offline (approved) payments
          const totalPaid = (invoice.student_payments || []).reduce(
            (sum: number, payment: any) =>
              payment.payment_status === "completed" || payment.payment_status === "approved"
                ? sum + Number(payment.amount || 0)
                : sum,
            0
          );

          // Calculate remaining balance
          const balancePayable = Math.max(0, netPayable - totalPaid);

          // Determine status based on actual payments and backend status
          let status = invoice.invoice_payment_status || "pending";
          if (totalPaid >= netPayable && netPayable > 0) {
            status = "completed";
          } else if (totalPaid > 0) {
            status = "partial";
          } else if (invoice.invoice_payment_status) {
            status = invoice.invoice_payment_status;
          }

          return {
            id: invoice.id,
            stageId: invoice.stage_id,
            stageName:
              invoice.type === "registrationfee"
                ? "Registration"
                : invoice.stage?.name || "N/A",
            type: invoice.type || "coursefee",
            dueDate: formatDate(invoice.due_date),
            amount: rawAmount,
            student_payments: invoice.student_payments || [],
            status: status,
            paymentDate: paymentDate,
            paymentMode: paymentMode,
            reference: `INV-${invoice.id}`,
            courseName: invoice.course.name,
            invoiceDate: formatDate(invoice.invoice_date),
            discount: discountAmount,
            originalPayable: netPayable,
            paidAmount: totalPaid,
            payable: balancePayable,
            invoice_number: invoice.invoice_number,
            center: invoice.center,
            stage: invoice.stage,
            stageOrder: invoice.stage?.order_in_course ?? null,
            // Extract remarks from the most recent approved offline payment
            offlineRemarks: (() => {
              const offlineWithRemarks = (invoice.student_payments || [])
                .filter((p: any) => p.payment_type === "offline" && p.remarks)
                .map((p: any) => p.remarks as string);
              return offlineWithRemarks.length > 0 ? offlineWithRemarks[offlineWithRemarks.length - 1] : "";
            })(),
            // Extract receipt_group_id from approved offline payments for grouped receipt display
            receipt_group_id: (() => {
              const offlineWithGroup = (invoice.student_payments || [])
                .filter((p: any) => p.payment_type === "offline" && p.receipt_group_id)
                .map((p: any) => p.receipt_group_id as string);
              return offlineWithGroup.length > 0 ? offlineWithGroup[offlineWithGroup.length - 1] : null;
            })(),
          };
        })
        // Sort the invoice rows: Registration fee first, then stages in sequence order (Level 1, 2, 3...)
        .sort((a: any, b: any) => {
          if (a.type === "registrationfee" && b.type !== "registrationfee")
            return -1;
          if (a.type !== "registrationfee" && b.type === "registrationfee")
            return 1;

          // Check explicit stage order if present
          const orderA = a.stageOrder ?? a.stage?.order_in_course;
          const orderB = b.stageOrder ?? b.stage?.order_in_course;
          if (orderA !== undefined && orderA !== null && orderB !== undefined && orderB !== null && orderA !== orderB) {
            return Number(orderA) - Number(orderB);
          }

          // Extract number from stage name (e.g. "Level 1" -> 1, "Level 2" -> 2, "Level 10" -> 10)
          const numA = (a.stageName || "").match(/\d+/);
          const numB = (b.stageName || "").match(/\d+/);
          if (numA && numB) {
            const parsedA = parseInt(numA[0], 10);
            const parsedB = parseInt(numB[0], 10);
            if (parsedA !== parsedB) return parsedA - parsedB;
          }

          // Fallback natural sort
          return (
            (a.stageName || "").localeCompare(b.stageName || "", undefined, {
              numeric: true,
              sensitivity: "base",
            }) || (a.id - b.id)
          );
        })
    );
  }, [invoices]);

  // Calculate invoice summary with correct paid amounts
  const summary: StudentInvoiceSummary = useMemo(() => {
    let totalAmount = 0;
    let totalPayable = 0;
    let paidAmount = 0;
    let pendingAmount = 0;
    let totalDiscount = 0;
    let paidInvoiceCount = 0;
    let pendingInvoiceCount = 0;

    invoiceRows.forEach((row: any) => {
      totalAmount += Number(row.amount || 0);
      totalDiscount += Number(row.discount || 0);
      paidAmount += Number(row.paidAmount || 0);

      if (row.status === "completed") {
        paidInvoiceCount++;
      } else {
        pendingInvoiceCount++;
      }
    });

    const netPayable = Math.max(0, totalAmount - totalDiscount);
    totalPayable = netPayable;
    pendingAmount = Math.max(0, netPayable - paidAmount);

    return {
      totalAmount,
      totalPayable,
      paidAmount,
      pendingAmount,
      invoiceCount: invoiceRows.length,
      paidInvoiceCount,
      pendingInvoiceCount,
      totalDiscount,
    };
  }, [invoiceRows]);

  return {
    invoices,
    invoiceRows,
    isLoading,
    error,
    refetch,
    summary,
    formatDate,
    getInvoiceById,
  };
};